// Socket.io client for the AI feature.
//
// Two backend versions run in parallel during the A/B comparison phase:
//   - 'ai'  → Socket.io namespace /ai   (legacy, Phase 1)
//   - 'ai2' → Socket.io namespace /ai2  (rebuilt, Phase 1 acceptance)
//
// The UI picks one via a dropdown. We cache one socket instance per
// version and never share connections across versions, so a v1 session
// and a v2 session can run simultaneously without crossing wires.
//
// Auth payload is built from localStorage — same source the existing
// axios interceptor uses (jwt_token, selected_env).

import { io } from "socket.io-client";

const sockets = new Map(); // version → socket

function buildAuth() {
  // uiMode is read here so refreshAuth() picks up Dev/Prod toggles without
  // any caller wiring. Server-side socket middleware validates it.
  const storedUiMode = localStorage.getItem("ai_ui_mode");
  // confidentialAccess (Sprint 7, B-CONF-3) is read from the same handshake the
  // /ai-assistant namespace inspects. Only meaningful for the AI Assistant engine;
  // the legacy /ai and /ai2 namespaces ignore it. Fail-closed: only the literal
  // "true" grants access, so confidential records stay hidden by default.
  const storedConfidential = localStorage.getItem("ai_confidential_access");
  return {
    token: localStorage.getItem("jwt_token"),
    env: localStorage.getItem("selected_env") || "dev",
    uiMode: storedUiMode === "prod" ? "prod" : "dev",
    confidentialAccess: storedConfidential === "true",
  };
}

function namespaceFor(version) {
  if (version === "ai-assistant") return "/ai-assistant";
  if (version === "ai2") return "/ai2";
  return "/ai";
}

export function getSocket(aiVersion = "ai") {
  if (sockets.has(aiVersion)) return sockets.get(aiVersion);

  const ns = namespaceFor(aiVersion);
  const socket = io(ns, {
    autoConnect: true,
    auth: buildAuth(),
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
  });

  socket.on("connect", () => {
    console.log(`[ai-socket:${aiVersion}] connected`, socket.id);
  });
  socket.on("connect_error", (err) => {
    console.error(`[ai-socket:${aiVersion}] connect_error:`, err.message, err.data);
  });
  socket.on("disconnect", (reason) => {
    console.log(`[ai-socket:${aiVersion}] disconnected:`, reason);
  });

  sockets.set(aiVersion, socket);
  return socket;
}

// Force a reconnect with refreshed auth (e.g. after env change or login).
// Refreshes ALL active version sockets.
export function refreshAuth() {
  for (const [, socket] of sockets) {
    socket.auth = buildAuth();
    socket.disconnect();
    socket.connect();
  }
}

export function disconnectAll() {
  for (const [, socket] of sockets) socket.disconnect();
  sockets.clear();
}
